import { Box3, Mesh, Vector3, type AnimationClip, type Material, type Object3D, type Texture } from "three";
import { TILE_FOOTPRINT } from "../viewerConstants";

export interface ModelInfo {
  triangles: number;
  textures: number;
  slots: string[];
  clipNames: string[];
  size: { x: number; y: number; z: number };
}

export function meshMaterials(mesh: Mesh): Material[] {
  return Array.isArray(mesh.material) ? mesh.material : [mesh.material];
}

export function nameMaterialSlots(root: Object3D): Map<Material, string> {
  const slotNames = new Map<Material, string>();
  const taken = new Set<string>();

  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    for (const material of meshMaterials(object)) {
      if (slotNames.has(material)) continue;
      const preferred = material.name || `Material ${slotNames.size + 1}`;
      let name = preferred;
      for (let copy = 2; taken.has(name); copy += 1) name = `${preferred} (${copy})`;
      taken.add(name);
      slotNames.set(material, name);
    }
  });
  return slotNames;
}

export function readModelInfo(root: Object3D, clips: readonly AnimationClip[]): ModelInfo {
  let triangles = 0;
  const textures = new Set<Texture>();

  root.traverse((object) => {
    if (!(object instanceof Mesh)) return;
    const { index, attributes } = object.geometry;
    triangles += (index?.count ?? attributes.position.count) / 3;
    for (const material of meshMaterials(object)) {
      for (const property of Object.values(material)) {
        if ((property as Texture | null)?.isTexture) textures.add(property as Texture);
      }
    }
  });

  const size = new Box3().setFromObject(root).getSize(new Vector3());
  return {
    triangles: Math.round(triangles),
    textures: textures.size,
    slots: [...nameMaterialSlots(root).values()],
    clipNames: clips.map((clip) => clip.name),
    size: { x: size.x, y: size.y, z: size.z }
  };
}

export function scaleToFitTile(info: ModelInfo): number {
  const widest = Math.max(info.size.x, info.size.z);
  return widest > 0 ? TILE_FOOTPRINT / widest : 1;
}
